import { WaNote } from '@common-models/WaNote';
import { userDisplayName } from '@common-models/user';
import { DBWaNoteThunks, userSelectors } from "@common-reducers/DBServiceThunks";
import { setEditModeByExtraDataId } from '@common-reducers/ExtraDataReducer';
import { selectMergedContactMap } from '@common-reducers/WhatsAppReducer';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { getWaChatContactDisplayName } from '@/common/utils/display-name-utils';
import { Box, Stack, Chip } from '@mui/material';
import moment from 'moment';
import { MoreHorizontal, Pencil, Trash2, MessageCircle, AlertCircle } from 'lucide-react';
import PersonAvatar from '@common-components/tasks/person-avatar/person-avatar';
import SlateEditor, { RenderUpdate } from '@common-components/tasks/extraData/lazy-slate';
import classes from "@common-components/tasks/extraData/editor/thread.module.scss";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { useState, useEffect } from 'react';
import { postMessageWithResponse } from '@common/services/post-message-to-whatsapp-functions';
import { fetchAndCacheChatNameThunk, fetchAndCacheProfilePicUrlsThunk } from '@extension-context-content/thunks/WhatsappThunks';

type Props = {
    item: WaNote;
    showContactName?: boolean;
    currentChatId?: string;
}

export default function WaNoteItem({ item, showContactName = false, currentChatId }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const editModeByExtraDataId = useAppSelector(
        (state) => state.ExtraDataReducer.editModeByExtraDataId
    );
    const currentUser = useAppSelector(state => state.UserReducer.userId);

    // Portal logic for correct z-indexing
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    useEffect(() => {
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    // Select user profile using selector
    const profile = useAppSelector(state => userSelectors.selectById(state, item.owner as string));

    // Get contact info for showing contact name in "all notes" mode
    const mergedContactMap = useAppSelector(selectMergedContactMap);
    const contact = showContactName ? mergedContactMap[item.chatId] : null;
    const contactDisplayName = showContactName
        ? getWaChatContactDisplayName(contact, { fallback: item.chatId?.split('@')[0] || 'Unknown' })
        : '';

    const cachedForEffect = item.chatId
        ? (mergedContactMap[item.chatId] as { name?: string; imgUrl?: string; profilePicLastUpdate?: string } | undefined)
        : undefined;
    const cachedName = cachedForEffect?.name;
    const cachedImgUrl = cachedForEffect?.imgUrl;
    const cachedPicLastUpdate = cachedForEffect?.profilePicLastUpdate;

    useEffect(() => {
        if (!showContactName || !item.chatId) return;
        if (!cachedName) dispatch(fetchAndCacheChatNameThunk(item.chatId));
        // Only fetch profile pic if we never tried (no profilePicLastUpdate)
        // and we don't already have one. Otherwise we'd re-fetch every render
        // for chats that genuinely have no pic (e.g. groups w/o image).
        if (!cachedImgUrl && !cachedPicLastUpdate) {
            dispatch(fetchAndCacheProfilePicUrlsThunk(item.chatId));
        }
    }, [dispatch, item.chatId, showContactName, cachedName, cachedImgUrl, cachedPicLastUpdate]);

    const isCurrentContact = item.chatId === currentChatId;

    const canDelete = item.owner === currentUser || !item.owner;

    const handleGoToContact = async () => {
        if (item.chatId) {
            try {
                await postMessageWithResponse({
                    cmd: "currentChat",
                    data: {
                        id: { _serialized: item.chatId }
                    }
                });
            } catch (error) {
                console.error("Failed to navigate to chat:", error);
            }
        }
    };

    const updateNote = (editorValue: any, isDraft: boolean) => {
        if (isDraft) return;
        const updatedNote = {
            _id: item._id,
            extraData: editorValue.editorValue,
        };

        dispatch(DBWaNoteThunks.patch({
            entity: updatedNote as any
        }));
        dispatch(setEditModeByExtraDataId(""))
    }

    const cancelUpdate = () => {
        dispatch(setEditModeByExtraDataId(""))
    }

    const handleEdit = () => {
        if (item._id) dispatch(setEditModeByExtraDataId(item._id));
    };

    const confirmDelete = () => {
        dispatch(DBWaNoteThunks.patch({
            entity: {
                _id: item._id,
                deleted: true,
            } as any
        }));
        setIsDeleteDialogOpen(false);
    };

    return (
        <div
            style={{
                borderRadius: "14px",
                marginBottom: "20px",
                backgroundColor: 'var(--color-surface)',
                boxShadow: "0 2px 7px -2px rgb(0 0 0 / 15%)",
                position: "relative",
                border: isCurrentContact && showContactName ? '2px solid var(--color-primary, #1976d2)' : 'none',
            }}
        >
            {/* Contact Name Badge - shown in "All Notes" mode */}
            {showContactName && (
                <Box
                    sx={{
                        px: 1.5,
                        pt: 1,
                        pb: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Chip
                        label={contactDisplayName}
                        size="small"
                        onClick={handleGoToContact}
                        sx={{
                            fontSize: '0.7rem',
                            height: '22px',
                            cursor: 'pointer',
                            border: '1px solid var(--color-border)',
                            backgroundColor: isCurrentContact
                                ? 'var(--color-primary)'
                                : 'var(--color-surface-muted)',
                            color: isCurrentContact
                                ? 'var(--color-on-primary, #ffffff)'
                                : 'var(--color-text)',
                            '& .MuiChip-label': {
                                color: 'inherit',
                            },
                            '&:hover': {
                                backgroundColor: isCurrentContact
                                    ? 'var(--color-primary-hover, var(--color-primary))'
                                    : 'var(--color-surface-hover, var(--color-surface-muted))',
                                filter: isCurrentContact ? 'brightness(0.92)' : 'brightness(0.96)',
                            },
                        }}
                    />
                    {isCurrentContact && (
                        <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>
                            {t('notes.currentIndicator')}
                        </span>
                    )}
                </Box>
            )}

            <Box className={classes.thread_header}>
                <div className={classes.author_profile}>
                    <Box sx={{ marginInlineEnd: 1 }}>
                        <PersonAvatar
                            size={24}
                            email={profile?.email}
                            profileImg={profile?.profileImg}
                            lastName={profile?.lastName}
                            firstName={profile?.firstName}
                            toolTip={true}
                            isName={false}
                        />
                    </Box>
                    <Stack spacing={0.2} sx={{ fontFamily: "Inter" }}>
                        <div style={{ color: "var(--color-text)", fontSize: '12px', fontWeight: "600" }} >
                             {profile ? userDisplayName(profile) : t('notes.unknownUser')}
                        </div>

                        <div style={{ color: "var(--color-text-muted)", fontSize: '11px' }} >
                            {item?.updatedAt ? moment(item.updatedAt).format("DD/MM/yyyy") : moment(new Date()).format("DD/MM/yyyy")}
                        </div>
                    </Stack>
                </div>
                <div className={classes.aside_right}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors outline-none" style={{ color: 'var(--color-text-muted)' }}>
                                <MoreHorizontal size={20} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            container={portalContainer || localPortalContainer}
                            className="w-48 z-[99999]"
                        >
                            {showContactName && !isCurrentContact && (
                                <DropdownMenuItem onClick={handleGoToContact} className="cursor-pointer">
                                    <MessageCircle className="mr-2 h-4 w-4" />
                                    {t('notes.goToChat')}
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={handleEdit} className="cursor-pointer">
                                <Pencil className="mr-2 h-4 w-4" />
                                {t('extraDataMenu.editUpdate')}
                            </DropdownMenuItem>
                            {canDelete && (
                                <DropdownMenuItem
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        setIsDeleteDialogOpen(true);
                                    }}
                                    className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    {t('extraDataMenu.deleteUpdate')}
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </Box>

            {editModeByExtraDataId === item._id ? (
                <SlateEditor value={item.extraData} onValueUpdate={updateNote} addBorder={false} onCancel={cancelUpdate} />
            ) : (
                <Box className={classes.thread_main_component} sx={{ fontFamily: "'Inter', 'Assistant'", color: 'var(--color-text)' }}>
                    <RenderUpdate update={item.extraData} />
                </Box>
            )}

            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md p-6 sm:p-8">
                    <DialogHeader className="space-y-3">
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            {t('extraDataMenu.deleteConfirmation.title', 'Delete note?')}
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground">
                            {t(
                                'extraDataMenu.deleteConfirmation.description',
                                'Are you sure you want to delete this note? This action cannot be undone.'
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('extraDataMenu.deleteConfirmation.cancel', 'Cancel')}
                        </Button>
                        <Button variant="destructive" onClick={confirmDelete}>
                            {t('extraDataMenu.deleteConfirmation.confirm', 'Delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
